import { useEffect, useMemo, useState } from 'react';
import { collection, collectionGroup, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useCRM } from '../utils/CRMContext';
import { Activity, EnhancedDeal, EngagementLetter, Lead } from '../types';
import { isValidTimestamp, MISSING_TIMESTAMP_SENTINEL, LeadLastUpdatedInfo, LeadLastUpdatedSource } from '../utils/leadsHubUtils';

export type { LeadLastUpdatedInfo, LeadLastUpdatedSource };

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && value !== null && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    try {
      const d = (value as { toDate: () => Date }).toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function maxCandidate(
  current: LeadLastUpdatedInfo | null,
  at: Date | null,
  source: LeadLastUpdatedSource,
  label: string,
): LeadLastUpdatedInfo | null {
  if (!at || at.getTime() === MISSING_TIMESTAMP_SENTINEL.getTime()) return current;
  if (!current || at.getTime() > current.at.getTime()) {
    return { at, source, label };
  }
  return current;
}

export function buildLastUpdatedByLead(
  leads: Lead[],
  activities: Activity[],
  enhancedDeals: EnhancedDeal[],
  engagementLetters: EngagementLetter[],
  documentLatestByLead: Map<string, Date>,
  commentLatestByLead: Map<string, Date>,
): Map<string, LeadLastUpdatedInfo> {
  const map = new Map<string, LeadLastUpdatedInfo>();

  for (const lead of leads) {
    if (!lead.id) continue;

    let best: LeadLastUpdatedInfo | null = null;

    if (isValidTimestamp(lead.updatedAt)) {
      best = maxCandidate(best, lead.updatedAt, 'lead', 'Lead updated');
    }
    if (isValidTimestamp(lead.createdAt)) {
      best = maxCandidate(best, lead.createdAt, 'lead', 'Lead created');
    }

    for (const activity of activities) {
      if (activity.entityType !== 'lead' || activity.entityId !== lead.id) continue;
      const label =
        activity.type === 'call'
          ? 'Call logged'
          : activity.type === 'email'
            ? 'Email activity'
            : activity.type === 'meeting'
              ? 'Meeting'
              : activity.type === 'note'
                ? 'Note added'
                : 'Activity logged';
      best = maxCandidate(best, toDate(activity.updatedAt), 'activity', label);
      best = maxCandidate(best, toDate(activity.completedAt), 'activity', label);
      best = maxCandidate(best, toDate(activity.createdAt), 'activity', label);
    }

    for (const deal of enhancedDeals) {
      if (deal.leadId !== lead.id) continue;
      best = maxCandidate(best, toDate(deal.updatedAt), 'deal', 'Project / proposal updated');
      best = maxCandidate(best, toDate(deal.createdAt), 'deal', 'Project created');
    }

    for (const letter of engagementLetters) {
      if (letter.leadId !== lead.id) continue;
      best = maxCandidate(best, toDate(letter.updatedAt), 'engagement', 'Engagement letter updated');
      best = maxCandidate(best, toDate(letter.signedAt), 'engagement', 'Agreement signed');
      best = maxCandidate(best, toDate(letter.sentAt), 'engagement', 'Engagement letter sent');
    }

    const docAt = documentLatestByLead.get(lead.id);
    if (docAt) {
      best = maxCandidate(best, docAt, 'document', 'Document added');
    }

    const commentAt = commentLatestByLead.get(lead.id);
    if (commentAt) {
      best = maxCandidate(best, commentAt, 'comment', 'Comment added');
    }

    if (best) {
      map.set(lead.id, best);
    }
  }

  return map;
}

export function useLeadLastUpdated(activities: Activity[]): Map<string, LeadLastUpdatedInfo> {
  const { leads, enhancedDeals, engagementLetters } = useCRM();
  const [documentLatestByLead, setDocumentLatestByLead] = useState<Map<string, Date>>(() => new Map());
  const [commentLatestByLead, setCommentLatestByLead] = useState<Map<string, Date>>(() => new Map());

  useEffect(() => {
    const q = query(collection(db, 'leadDocuments'), orderBy('uploadedAt', 'desc'));
    const unsub = onSnapshot(q, snapshot => {
      const next = new Map<string, Date>();
      snapshot.docs.forEach(docSnap => {
        const leadId = docSnap.data().leadId as string | undefined;
        const at = toDate(docSnap.data().uploadedAt);
        if (!leadId || !at) return;
        const prev = next.get(leadId);
        if (!prev || at.getTime() > prev.getTime()) {
          next.set(leadId, at);
        }
      });
      setDocumentLatestByLead(next);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(collectionGroup(db, 'comments'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      snapshot => {
        const next = new Map<string, Date>();
        snapshot.docs.forEach(docSnap => {
          const leadId = docSnap.data().leadId as string | undefined;
          const at = toDate(docSnap.data().createdAt);
          if (!leadId || !at) return;
          const prev = next.get(leadId);
          if (!prev || at.getTime() > prev.getTime()) {
            next.set(leadId, at);
          }
        });
        setCommentLatestByLead(next);
      },
      err => {
        console.warn('[useLeadLastUpdated] comments collectionGroup listener failed:', err);
      },
    );
    return unsub;
  }, []);

  return useMemo(
    () =>
      buildLastUpdatedByLead(
        leads,
        activities,
        enhancedDeals,
        engagementLetters,
        documentLatestByLead,
        commentLatestByLead,
      ),
    [leads, activities, enhancedDeals, engagementLetters, documentLatestByLead, commentLatestByLead],
  );
}
